import { FC, useState } from "react";
import { Button } from "./Button";
import { NumericInput } from "./NumericInput";
import { Device } from "../models";
import { Devices } from "../api";

/** The form's current values, converted to the stored (firmware-convention) units. */
export type DeviceIntrinsicsValues = {
    fusionOffsetPitchRadians: number;
    fusionOffsetRollRadians: number;
    fusionOffsetYawRadians: number;
    fusionEstimatedReturnHeight: number;
    /** Device mounting height in meters (Device.height). */
    height: number;
};

export type DeviceIntrinsicsProps = {
    device: Device;
    onChange?: () => any;
    onSave?: (device: Device) => Promise<void>;
    onSuccess?: (title: string, message: string) => void;
    /** Fires on every edit (and reset) with the unsaved values — lets a live preview track the form. */
    onValuesChange?: (values: DeviceIntrinsicsValues) => void;
};

// Roll and yaw are stored with the same sign the UI shows; pitch is negated
// (stored radians are up-positive, the UI shows down-tilt as positive).
const radiansToDegrees = (radians: number) => radians * (180 / Math.PI);
const degreesToRadians = (degrees: number) => degrees * (Math.PI / 180);
const pitchRadiansToDegrees = (radians: number) => radians * (-180 / Math.PI);
const pitchDegreesToRadians = (degrees: number) => degrees * (-Math.PI / 180);

export const DeviceIntrinsics: FC<DeviceIntrinsicsProps> = ({ device, onChange, onSave, onSuccess, onValuesChange }) => {
    const [fusionOffsetRoll, setFusionOffsetRoll] = useState(radiansToDegrees(device.fusionOffsetRoll ?? 0));
    const [fusionOffsetPitch, setFusionOffsetPitch] = useState(pitchRadiansToDegrees(device.fusionOffsetPitch ?? 0));
    const [fusionOffsetYaw, setFusionOffsetYaw] = useState(radiansToDegrees(device.fusionOffsetYaw ?? 0));
    const [fusionEstimatedReturnHeight, setFusionEstimatedReturnHeight] = useState(device.fusionEstimatedReturnHeight ?? 0);
    const [deviceHeight, setDeviceHeight] = useState(device.height ?? 0);
    const [hasChanges, setHasChanges] = useState(false);

    const reportValues = (values: { rollDeg: number; pitchDeg: number; yawDeg: number; returnHeight: number; heightM: number }) => {
        onValuesChange?.({
            fusionOffsetRollRadians: degreesToRadians(values.rollDeg),
            fusionOffsetPitchRadians: pitchDegreesToRadians(values.pitchDeg),
            fusionOffsetYawRadians: degreesToRadians(values.yawDeg),
            fusionEstimatedReturnHeight: values.returnHeight,
            height: values.heightM,
        });
    };

    const currentValues = () => ({
        rollDeg: fusionOffsetRoll,
        pitchDeg: fusionOffsetPitch,
        yawDeg: fusionOffsetYaw,
        returnHeight: fusionEstimatedReturnHeight,
        heightM: deviceHeight,
    });

    const handleSave = async () => {
        const updatedDevice = {
            ...device,
            fusionOffsetRoll: degreesToRadians(fusionOffsetRoll),
            fusionOffsetPitch: pitchDegreesToRadians(fusionOffsetPitch),
            fusionOffsetYaw: degreesToRadians(fusionOffsetYaw),
            fusionEstimatedReturnHeight,
            height: deviceHeight,
            homographySourcePoints: undefined,
            homographyDestinationPoints: undefined,
        } as Device;

        if (onSave) {
            await onSave(updatedDevice);
        } else {
            await Devices.save(updatedDevice);
        }

        if (onChange) {
            await onChange();
        }

        if (onSuccess) {
            onSuccess("Saved", "Device intrinsics have been saved!");
        }
        setHasChanges(false);
    };

    const handleReset = () => {
        const rollDeg = radiansToDegrees(device.fusionOffsetRoll ?? 0);
        const pitchDeg = pitchRadiansToDegrees(device.fusionOffsetPitch ?? 0);
        const yawDeg = radiansToDegrees(device.fusionOffsetYaw ?? 0);
        const returnHeight = device.fusionEstimatedReturnHeight ?? 0;
        const heightM = device.height ?? 0;
        setFusionOffsetRoll(rollDeg);
        setFusionOffsetPitch(pitchDeg);
        setFusionOffsetYaw(yawDeg);
        setFusionEstimatedReturnHeight(returnHeight);
        setDeviceHeight(heightM);
        setHasChanges(false);
        reportValues({ rollDeg, pitchDeg, yawDeg, returnHeight, heightM });
    };

    const updateValue = (setter: (value: number) => void) => (value: number) => {
        setter(value);
        setHasChanges(true);
    };

    const handleRollChange = (value: number) => {
        updateValue(setFusionOffsetRoll)(value);
        reportValues({ ...currentValues(), rollDeg: value });
    };

    const handlePitchChange = (value: number) => {
        updateValue(setFusionOffsetPitch)(value);
        reportValues({ ...currentValues(), pitchDeg: value });
    };

    const handleYawChange = (value: number) => {
        updateValue(setFusionOffsetYaw)(value);
        reportValues({ ...currentValues(), yawDeg: value });
    };

    const handleReturnHeightChange = (value: number) => {
        updateValue(setFusionEstimatedReturnHeight)(value);
        reportValues({ ...currentValues(), returnHeight: value });
    };

    const handleDeviceHeightChange = (value: number) => {
        updateValue(setDeviceHeight)(value);
        reportValues({ ...currentValues(), heightM: value });
    };

    return (
        <div className="p-6">
            <h2 className="text-xl font-semibold mb-4">Device Intrinsics Configuration</h2>

            <div className="grid grid-cols-1 gap-4 max-w-md">
                <NumericInput
                    label="Fusion Offset Roll (degrees)"
                    id="fusionOffsetRoll"
                    value={fusionOffsetRoll}
                    onChange={(val) => val !== undefined && handleRollChange(val)}
                    className="w-full"
                    allowFloat={true}
                />

                <NumericInput
                    label="Fusion Offset Pitch (degrees)"
                    id="fusionOffsetPitch"
                    value={fusionOffsetPitch}
                    onChange={(val) => val !== undefined && handlePitchChange(val)}
                    className="w-full"
                    allowFloat={true}
                />

                <NumericInput
                    label="Fusion Offset Yaw (degrees)"
                    id="fusionOffsetYaw"
                    value={fusionOffsetYaw}
                    onChange={(val) => val !== undefined && handleYawChange(val)}
                    className="w-full"
                    allowFloat={true}
                />

                <NumericInput
                    label="Device Height (meters)"
                    id="deviceHeight"
                    value={deviceHeight}
                    onChange={(val) => val !== undefined && handleDeviceHeightChange(val)}
                    className="w-full"
                    allowFloat={true}
                />

                <NumericInput
                    label="Estimated Return Height (meters)"
                    id="estimatedReturnHeight"
                    value={fusionEstimatedReturnHeight}
                    onChange={(val) => val !== undefined && handleReturnHeightChange(val)}
                    className="w-full"
                    allowFloat={true}
                />
            </div>

            {hasChanges && (
                <div className="flex gap-2 mt-6">
                    <Button onClick={handleSave}>Save</Button>
                    <Button onClick={handleReset} className="bg-gray-500 hover:bg-gray-600">
                        Reset
                    </Button>
                </div>
            )}
        </div>
    );
};
